import { z } from 'zod';

import { TEMPLATE_NAMES } from '../core/assetType.js';

/**
 * Shape for the content-assets registry data.
 *
 * Every list is exported by Phoenix and they are the same shape: each entry is the
 * asset itself — its slug, and beside it everything the asset's family says
 * about it (name, kind, and for a character its gender, ethnicity, occupations, ...).
 * The entry's own `templates` says where it may appear, which is what makes a
 * classroom background a lesson's rather than a story's. A character starts
 * with every template chosen, so it may appear anywhere until someone narrows
 * its list. The validator owns none of the other fields — the family does — so
 * only `slug` and `templates` are required here and the rest rides along for
 * whichever rule or tool wants it.
 *
 * `buildRegistries` groups the entries by template; `dil` (day-in-life) takes
 * story's list for a kind when no entry of that kind names it.
 *
 * `families` is what each kind's description is made of, exactly as Phoenix
 * serves it from `GET /content-assets/kinds`: the field names, their shapes
 * and the choices a choice field allows. It travels with the lists so a
 * description — an exported entry's, or a story's request for a character —
 * can be checked offline against the definitions it was written to.
 *
 * (The career-clusters and consideration-areas registries remain flat arrays —
 * see `slugList.ts`.)
 */
export const ContentAssetEntry = z.looseObject({
  slug: z.string().min(1),
  templates: z.array(z.enum(TEMPLATE_NAMES)).min(1),
});
export type ContentAssetEntry = z.infer<typeof ContentAssetEntry>;

/** An entry of a kind that needs no templates (see `kindNeedsTemplates`): it may name none. */
const EntryNamingAnyTemplates = ContentAssetEntry.extend({
  templates: z.array(z.enum(TEMPLATE_NAMES)),
});

/** The shapes a family field can take; mirrors Phoenix's ContentAssetFieldType. */
export const FAMILY_FIELD_TYPES = ['string', 'list', 'choice', 'choices'] as const;
export type FamilyFieldType = (typeof FAMILY_FIELD_TYPES)[number];

export const ContentAssetFamilyField = z.looseObject({
  name: z.string().min(1),
  label: z.string(),
  type: z.enum(FAMILY_FIELD_TYPES),
  options: z.array(z.string()),
  default: z.unknown(),
});
export type ContentAssetFamilyField = z.infer<typeof ContentAssetFamilyField>;

export const ContentAssetFamily = z.looseObject({
  kind: z.string().min(1),
  label: z.string(),
  fields: z.array(ContentAssetFamilyField),
});
export type ContentAssetFamily = z.infer<typeof ContentAssetFamily>;

/**
 * Each kind's list in the registry, keyed by the kind whose assets it holds, in the order the
 * file carries them. This is the one place a list is named: the schema below, the per-template
 * sets `buildRegistries` builds, and hydra's writer and save counts are all read off it, so a
 * new kind is one line here (and the kind itself in Phoenix's `ContentAssetKind`, whose
 * `registryList()` must name the same list).
 */
export const CONTENT_ASSET_REGISTRY_LIST = {
  background: 'backgrounds',
  character: 'characters',
  /** Images that are neither: card pictures, say. A story's slug for one is checked against `all`, which holds every one of them whatever templates it names. */
  other: 'others',
} as const;
export type ContentAssetRegistryKind = keyof typeof CONTENT_ASSET_REGISTRY_LIST;
export type ContentAssetRegistryList = (typeof CONTENT_ASSET_REGISTRY_LIST)[ContentAssetRegistryKind];

/** Every list, in the order the file carries them. */
export const CONTENT_ASSET_REGISTRY_LISTS = Object.values(CONTENT_ASSET_REGISTRY_LIST) as readonly ContentAssetRegistryList[];

/** One value per list, keyed by the list, in the order the file carries them. */
export const mapRegistryLists = <T>(valueOf: (list: ContentAssetRegistryList) => T): Record<ContentAssetRegistryList, T> =>
  Object.fromEntries(CONTENT_ASSET_REGISTRY_LISTS.map(list => [list, valueOf(list)])) as Record<ContentAssetRegistryList, T>;

/**
 * The kinds whose assets may name no templates. An other image (a card picture) only has to
 * exist — a story may use one under any template — so Phoenix exports it even when it names
 * none, as a Studio upload does. This is the one statement of the rule on the TypeScript side:
 * the schema below and hollywood's library read it, and Phoenix's
 * `ContentAssetKind::needsTemplates` is held to it by `kindNeedsTemplatesCopies.test.ts`.
 */
const KINDS_NAMING_ANY_TEMPLATES: ReadonlySet<ContentAssetRegistryKind> = new Set(['other']);

/** Whether an asset of this kind has to name the templates it may appear in for Phoenix to export it. */
export const kindNeedsTemplates = (kind: ContentAssetRegistryKind): boolean => !KINDS_NAMING_ANY_TEMPLATES.has(kind);

const KIND_OF_LIST = Object.fromEntries(Object.entries(CONTENT_ASSET_REGISTRY_LIST).map(([kind, list]) => [list, kind])) as Record<ContentAssetRegistryList, ContentAssetRegistryKind>;

const listShapes = mapRegistryLists<z.ZodArray<typeof ContentAssetEntry>>(list =>
  z.array(kindNeedsTemplates(KIND_OF_LIST[list]) ? ContentAssetEntry : EntryNamingAnyTemplates));

/**
 * Loose at the root, and `others` may be absent: the lists come from a Phoenix that need not
 * be the same version as this validator, so a list that Phoenix does not serve yet reads as
 * empty, and one this validator does not know yet is carried along unread.
 */
export const ContentAssetsRegistry = z.looseObject({
  ...listShapes,
  others: listShapes.others.default([]),
  families: z.array(ContentAssetFamily),
});
export type ContentAssetsRegistry = z.infer<typeof ContentAssetsRegistry>;
/** The registry as a caller hands it over, before the schema fills in a list Phoenix left out. */
export type ContentAssetsRegistryData = z.input<typeof ContentAssetsRegistry>;
